// Your nights, the part without the app around it: what is kept on the device, which night a moment
// belongs to, and how saved or restored data is checked before it is trusted.
//
// A sighting keeps what cannot be worked out again later: a satellite its orbit data at the time (orbits
// drift), a shooting star the path that was drawn. Planets, stars and the Moon are worked out afresh for any
// date, so only their key is kept.
//
// 5 Oct 2026: Ahillah no longer shows planes. Nights saved before may hold them (kind 'aircraft', key
// plane:<hex>, with their positions while watched), so they are still read, backed up and restored as they were.
// The same day "What was that?" went, so no new shooting stars are drawn; nights saved before may hold them (kind
// 'meteor', key meteor:<time>, with the path drawn), and they are still read, backed up, restored, charted and relived.

// 10 Oct 2026: "I saw the crescent" keeps the new crescent of a Hijri month (kind 'crescent', key crescent:1448-05).
export type SightingKind = 'body' | 'star' | 'dso' | 'constellation' | 'satellite' | 'aircraft' | 'meteor' | 'shower' | 'small' | 'crescent';
const KINDS = new Set<SightingKind>(['body', 'star', 'dso', 'constellation', 'satellite', 'aircraft', 'meteor', 'shower', 'small', 'crescent']);

export interface NightPlace { name: string; lat: number; lon: number; height: number; timeZone?: string }
/** A plane's position report (older nights): when, where (degrees), height (metres), speed (knots), heading and climb (ft/min). */
export interface PlaneFix { t: number; lat: number; lon: number; alt: number | null; gs: number; track: number; rate: number }

export interface Sighting {
  id: string;
  /** A lasting key: body:Saturn, star:21421 (Hipparcos), dso:M45, con:Ori, sat:25544, plane:4ca7b4, shower:PER, meteor:<time>,
   *  crescent:1448-05 (the new crescent of a Hijri month). */
  key: string;
  /** The name as it was shown. */
  name: string;
  kind: SightingKind;
  /** When it was seen (ms). */
  t: number;
  place: NightPlace;
  /** Where it was in the sky, [altitude, azimuth] in degrees. */
  at?: [number, number];
  /** Satellites: the orbit data in use then (a CelesTrak OMM row), and the pass as seen, [alt, az] every 10 s. */
  orbit?: Record<string, string>;
  pass?: [number, number][];
  /** Planes, in older nights: the flight and its position reports while it was watched. */
  plane?: { flight?: string; type?: string; reg?: string; fixes: PlaneFix[] };
  /** Shooting stars, in older nights: the path drawn, [alt, az] pairs from start to end. */
  path?: [number, number][];
}

export interface Night {
  /** The evening's date where the night began (YYYY-MM-DD). */
  id: string;
  place: NightPlace;
  sightings: Sighting[];
  note: string;
  /** "Save this night": the moment kept, where the view was looking and how wide. */
  kept?: { t: number; at: [number, number]; fov: number };
  /** The forecast for the hour, as it was then (it cannot be looked up afterwards). */
  weather?: { cloud?: number; tempC?: number };
  /** How dark the sky was there (Bortle class, and sky brightness in mag/arcsec² when known). */
  sky?: { bortle: number; sqm: number | null };
  updated: number;
}

export type Nights = Record<string, Night>;

/**
 * The night a moment belongs to: the evening's date at the place, changing over at local (solar) noon,
 * so 1 am still counts as the night before.
 */
export function nightId(t: number, lon: number): string {
  return new Date(t + (lon / 15) * 3600e3 - 12 * 3600e3).toISOString().slice(0, 10);
}

/** "14 January 2027" (or without the year) for a night's id. */
export function nightLabel(id: string, withYear = true): string {
  const [y, m, d] = id.split('-').map(Number);
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: withYear ? 'numeric' : undefined, timeZone: 'UTC' }).format(Date.UTC(y, m - 1, d));
}

export const newId = (t: number) => `${Math.round(t).toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/** 9 pm (local solar time) on the evening a night id names, for a night with nothing timed on it. */
export function nightEvening(id: string, lon: number): number {
  const [y, m, d] = id.split('-').map(Number);
  return Date.UTC(y, m - 1, d, 21) - (lon / 15) * 3600e3;
}

/** The first and last moments of a night that anything happened (its evening, if nothing is timed). */
export function nightSpan(n: Night): [number, number] {
  const ts = n.sightings.map((s) => s.t);
  if (n.kept) ts.push(n.kept.t);
  if (!ts.length) {
    const e = nightEvening(n.id, n.place.lon);
    return [e, e];
  }
  return [Math.min(...ts), Math.max(...ts)];
}

/** The moment a night's picture shows: the one kept, else the middle sighting (else its evening). */
export function nightMoment(n: Night): number {
  if (n.kept && !n.sightings.length) return n.kept.t;
  const ts = n.sightings.map((s) => s.t).sort((a, b) => a - b);
  return ts.length ? ts[(ts.length - 1) >> 1] : n.kept?.t ?? nightEvening(n.id, n.place.lon);
}

/** Add a sighting to its night (made if new). Something already ticked that night is not added twice. */
export function withSighting(ns: Nights, s: Sighting, now = Date.now()): { nights: Nights; night: string; added: boolean } {
  const id = nightId(s.t, s.place.lon);
  const cur = ns[id];
  if (cur && !s.key.startsWith('meteor:') && cur.sightings.some((x) => x.key === s.key)) return { nights: ns, night: id, added: false };
  const night: Night = cur
    ? { ...cur, sightings: [...cur.sightings, s].sort((a, b) => a.t - b.t), updated: now }
    : { id, place: s.place, sightings: [s], note: '', updated: now };
  return { nights: { ...ns, [id]: night }, night: id, added: true };
}

/** Take a sighting off its night; a night left with nothing on it (no note, nothing kept) goes too. */
export function withoutSighting(ns: Nights, night: string, sightingId: string, now = Date.now()): Nights {
  const cur = ns[night];
  if (!cur) return ns;
  const sightings = cur.sightings.filter((s) => s.id !== sightingId);
  if (!sightings.length && !cur.note.trim() && !cur.kept) {
    const next = { ...ns };
    delete next[night];
    return next;
  }
  return { ...ns, [night]: { ...cur, sightings, updated: now } };
}

/** Everything ever seen, by key: the first sighting and how many nights it was seen on. */
export function firstSeen(ns: Nights): Map<string, { first: Sighting; count: number }> {
  const out = new Map<string, { first: Sighting; count: number }>();
  for (const n of Object.values(ns)) {
    for (const s of n.sightings) {
      const e = out.get(s.key);
      if (!e) out.set(s.key, { first: s, count: 1 });
      else {
        e.count++;
        if (s.t < e.first.t) e.first = s;
      }
    }
  }
  return out;
}

// ---------- Checking data from storage or a backup file ----------
const num = (v: unknown, lo = -Infinity, hi = Infinity): v is number => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;
const str = (v: unknown, max: number): v is string => typeof v === 'string' && v.length <= max;
const pair = (v: unknown): v is [number, number] => Array.isArray(v) && v.length === 2 && num(v[0], -90, 90) && num(v[1], -360, 720);
/**
 * A moment on the sky: when something was seen, or the moment a night keeps. Time travel shows any date, so any
 * moment a night's id can name (a four-digit year) is kept, 1969 as much as tonight. Times read from a clock (a
 * plane's reports, when a night was last changed) are checked as such, from 1970.
 */
const FIRST_MOMENT = Date.parse('0000-01-01T00:00:00Z'), LAST_MOMENT = Date.parse('9999-12-31T23:59:59.999Z');
const moment = (v: unknown): v is number => num(v, FIRST_MOMENT, LAST_MOMENT);
/** Time zones checked so far: an unknown name would make every date on a night's page fail to format. */
const zones = new Map<string, boolean>();
function zone(v: unknown): v is string {
  if (!str(v, 64) || !v) return false;
  let ok = zones.get(v);
  if (ok === undefined) {
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: v });
      ok = true;
    } catch {
      ok = false;
    }
    zones.set(v, ok);
  }
  return ok;
}

function cleanPlace(v: unknown): NightPlace | null {
  if (!v || typeof v !== 'object') return null;
  const p = v as Record<string, unknown>;
  if (!num(p.lat, -90, 90) || !num(p.lon, -180, 180) || !str(p.name, 160)) return null;
  const out: NightPlace = { name: p.name, lat: p.lat, lon: p.lon, height: num(p.height, -500, 9000) ? p.height : 0 };
  if (zone(p.timeZone)) out.timeZone = p.timeZone;
  return out;
}

function cleanPairs(v: unknown, max = 600): [number, number][] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter(pair).slice(0, max).map((q) => [q[0], q[1]] as [number, number]);
  return out.length ? out : undefined;
}

function cleanSighting(v: unknown): Sighting | null {
  if (!v || typeof v !== 'object') return null;
  const s = v as Record<string, unknown>;
  const place = cleanPlace(s.place);
  if (!place || !str(s.id, 48) || !s.id || !str(s.key, 160) || !s.key || !str(s.name, 160) || !KINDS.has(s.kind as SightingKind) || !moment(s.t)) return null;
  const out: Sighting = { id: s.id, key: s.key, name: s.name, kind: s.kind as SightingKind, t: s.t, place };
  if (pair(s.at)) out.at = [s.at[0], s.at[1]];
  if (s.orbit && typeof s.orbit === 'object' && !Array.isArray(s.orbit)) {
    const o = Object.entries(s.orbit as Record<string, unknown>).filter(([k, x]) => str(k, 40) && str(x, 200)).slice(0, 40) as [string, string][];
    if (o.length) out.orbit = Object.fromEntries(o);
  }
  const pass = cleanPairs(s.pass);
  if (pass) out.pass = pass;
  const path = cleanPairs(s.path);
  if (path) out.path = path;
  if (s.plane && typeof s.plane === 'object') {
    const p = s.plane as Record<string, unknown>;
    const fixes = (Array.isArray(p.fixes) ? p.fixes : [])
      .filter((f): f is PlaneFix => !!f && typeof f === 'object' && num((f as PlaneFix).t, 0) && num((f as PlaneFix).lat, -90, 90) && num((f as PlaneFix).lon, -180, 180) &&
        ((f as PlaneFix).alt === null || num((f as PlaneFix).alt, -1000, 30000)) && num((f as PlaneFix).gs, 0, 2000) && num((f as PlaneFix).track, -360, 720) && num((f as PlaneFix).rate, -20000, 20000))
      .slice(0, 400)
      .map((f) => ({ t: f.t, lat: f.lat, lon: f.lon, alt: f.alt, gs: f.gs, track: f.track, rate: f.rate }));
    out.plane = { fixes };
    if (str(p.flight, 16) && p.flight) out.plane.flight = p.flight;
    if (str(p.type, 60) && p.type) out.plane.type = p.type;
    if (str(p.reg, 16) && p.reg) out.plane.reg = p.reg;
  }
  return out;
}

function cleanNight(v: unknown): Night | null {
  if (!v || typeof v !== 'object') return null;
  const n = v as Record<string, unknown>;
  const place = cleanPlace(n.place);
  if (!place || !str(n.id, 10) || !/^\d{4}-\d{2}-\d{2}$/.test(n.id)) return null;
  const seen = new Set<string>();
  const sightings = (Array.isArray(n.sightings) ? n.sightings : [])
    .slice(0, 500)
    .map(cleanSighting)
    .filter((s): s is Sighting => !!s && !seen.has(s.id) && !!seen.add(s.id))
    .sort((a, b) => a.t - b.t);
  const out: Night = { id: n.id, place, sightings, note: str(n.note, 4000) ? n.note : '', updated: num(n.updated, 0) ? n.updated : 0 };
  const k = n.kept as Record<string, unknown> | undefined;
  // The field of view is clamped rather than checked: a view zoomed right in (0.05°) must not lose the night.
  if (k && typeof k === 'object' && moment(k.t) && pair(k.at) && num(k.fov)) out.kept = { t: k.t, at: [k.at[0], k.at[1]], fov: Math.min(200, Math.max(0.05, k.fov)) };
  const w = n.weather as Record<string, unknown> | undefined;
  if (w && typeof w === 'object') {
    const weather: Night['weather'] = {};
    if (num(w.cloud, 0, 100)) weather.cloud = w.cloud;
    if (num(w.tempC, -90, 60)) weather.tempC = w.tempC;
    if (Object.keys(weather).length) out.weather = weather;
  }
  const sk = n.sky as Record<string, unknown> | undefined;
  if (sk && typeof sk === 'object' && num(sk.bortle, 1, 9)) out.sky = { bortle: sk.bortle, sqm: num(sk.sqm, 10, 25) ? sk.sqm : null };
  if (!out.sightings.length && !out.note.trim() && !out.kept) return null;
  return out;
}

/** Nights from storage or a backup (an object keyed by night, or a list), with anything malformed left out. */
export function cleanNights(raw: unknown): Nights {
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw as Record<string, unknown>) : [];
  const out: Nights = {};
  for (const v of list.slice(0, 5000)) {
    const n = cleanNight(v);
    if (n && !out[n.id]) out[n.id] = n;
  }
  return out;
}

/** Bring a backup's nights in alongside the ones here: new nights are added, and new sightings join their night. */
export function mergeNights(here: Nights, incoming: Nights): { nights: Nights; nightsAdded: number; sightingsAdded: number } {
  const out = { ...here };
  let nightsAdded = 0, sightingsAdded = 0;
  for (const n of Object.values(incoming)) {
    const cur = out[n.id];
    if (!cur) {
      out[n.id] = n;
      nightsAdded++;
      sightingsAdded += n.sightings.length;
      continue;
    }
    const ids = new Set(cur.sightings.map((s) => s.id));
    const keys = new Set(cur.sightings.filter((s) => !s.key.startsWith('meteor:')).map((s) => s.key));
    const extra = n.sightings.filter((s) => !ids.has(s.id) && !keys.has(s.key));
    sightingsAdded += extra.length;
    out[n.id] = {
      ...cur,
      sightings: [...cur.sightings, ...extra].sort((a, b) => a.t - b.t),
      note: cur.note.trim() ? cur.note : n.note,
      kept: cur.kept ?? n.kept,
      weather: cur.weather ?? n.weather,
      sky: cur.sky ?? n.sky,
      updated: Math.max(cur.updated, n.updated),
    };
  }
  return { nights: out, nightsAdded, sightingsAdded };
}

/**
 * The "Seen it" log from before nights were kept: each thing with when it was first seen and the place's
 * name. Where it was seen is not known, so the place is the one here now unless the name differs, in which
 * case only its name is kept (with here's position, for drawing its sky).
 */
export function fromLogbook(log: Record<string, { t: number; place: string }>, here: NightPlace): Nights {
  let ns: Nights = {};
  for (const [key, e] of Object.entries(log)) {
    if (!e || !moment(e.t) || !str(key, 160)) continue;
    const kind = key.slice(0, key.indexOf(':')) as string;
    const k: SightingKind | null = kind === 'body' ? 'body' : kind === 'star' ? 'star' : kind === 'dso' ? 'dso' : kind === 'con' ? 'constellation' : kind === 'sat' ? 'satellite' : kind === 'shower' ? 'shower' : kind === 'small' ? 'small' : null;
    if (!k) continue;
    const place = !e.place || e.place === here.name ? here : { ...here, name: String(e.place).slice(0, 160) };
    ns = withSighting(ns, { id: newId(e.t), key, name: '', kind: k, t: e.t, place }, e.t).nights;
  }
  return ns;
}
